/**
 * 로그인 페이지 — Clipflow v3.6 Glass + Neon
 * 가운데 정렬 글래스 카드, ClipflowLogo 상단, 카카오/구글 소셜 로그인
 */
'use client'

import { useEffect, useState, type FormEvent } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ClipflowLogo } from '@/components/brand/ClipflowLogo'
import { V3Icon } from '@/components/icons/V3Icon'
import { useAuthStore } from '@/store/authStore'

/** 소셜 로그인 콜백 에러 코드 → 사용자 안내 메시지 */
const SOCIAL_ERROR_MESSAGES: Record<string, string> = {
  social_unavailable: '소셜 로그인이 현재 비활성화되어 있습니다.',
  kakao_denied: '카카오 로그인이 취소되었습니다.',
  kakao_token: '카카오 인증에 실패했습니다. 다시 시도해주세요.',
  kakao_server: '소셜 로그인 처리 중 오류가 발생했습니다.',
  google_denied: '구글 로그인이 취소되었습니다.',
  google_token: '구글 인증에 실패했습니다. 다시 시도해주세요.',
  google_server: '소셜 로그인 처리 중 오류가 발생했습니다.',
}

export default function LoginPage() {
  const router = useRouter()
  const { login, socialLogin, isLoading, error, clearError, setToken, fetchCurrentUser, setError } =
    useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // 소셜 로그인(카카오 등) 서버 콜백 처리: /login?token=…&refresh=… 또는 ?error=…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    const errCode = params.get('error')

    if (token) {
      // URL에서 토큰 제거(주소창 노출 방지) 후 인증 상태 반영
      window.history.replaceState({}, '', '/login')
      setToken(token)
      fetchCurrentUser()
        .then(() => router.push('/projects'))
        .catch(() => setError('로그인 정보를 불러오지 못했습니다.'))
    } else if (errCode) {
      window.history.replaceState({}, '', '/login')
      setError(SOCIAL_ERROR_MESSAGES[errCode] || '소셜 로그인에 실패했습니다.')
    }
    // 마운트 시 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    clearError()
    try {
      await login(email, password)
      router.push('/projects')
    } catch {
      /* 스토어가 에러 관리 */
    }
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        background: 'var(--v36-bg)',
        backgroundImage: 'var(--v36-noise)',
      }}
    >
      <div
        className="v36-fade-in"
        style={{ width: '100%', maxWidth: 440 }}
      >
        {/* 로고 */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Link href="/" aria-label="Clipflow 홈">
            <ClipflowLogo height={32} priority />
          </Link>
        </div>

        {/* 글래스 카드 */}
        <div
          className="v36-glass-strong"
          style={{
            padding: 32,
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1 className="v36-h2" style={{ margin: 0, marginBottom: 6 }}>
              로그인
            </h1>
            <p style={{ fontSize: 13, color: 'var(--v36-ink-3)', margin: 0 }}>
              계정에 로그인하여 편집을 계속하세요
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div
                className="v36-fade-in"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(255,77,79,0.10)',
                  border: '1px solid rgba(255,77,79,0.30)',
                  fontSize: 13,
                  color: 'var(--v36-error)',
                }}
              >
                <V3Icon name="bell" size={14} />
                <span>{error}</span>
              </div>
            )}

            <FormField label="이메일" htmlFor="email">
              <V3Icon
                name="type"
                size={14}
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--v36-ink-4)',
                  pointerEvents: 'none',
                }}
              />
              <input
                id="email"
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="v36-input"
                style={{ paddingLeft: 38 }}
              />
            </FormField>

            <FormField
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>비밀번호</span>
                  <Link
                    href="/forgot-password"
                    style={{
                      fontSize: 11,
                      color: 'var(--v36-violet)',
                      textDecoration: 'none',
                    }}
                  >
                    비밀번호 찾기
                  </Link>
                </div>
              }
              htmlFor="password"
            >
              <V3Icon
                name="settings"
                size={14}
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--v36-ink-4)',
                  pointerEvents: 'none',
                }}
              />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="비밀번호를 입력하세요"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="v36-input"
                style={{ paddingLeft: 38, paddingRight: 38 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--v36-ink-4)',
                  padding: 4,
                }}
              >
                <V3Icon name="eye" size={14} />
              </button>
            </FormField>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: 'var(--v36-ink-3)',
                cursor: 'pointer',
                marginTop: 4,
              }}
            >
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--v36-violet)' }}
              />
              <span>로그인 상태 유지</span>
            </label>

            <button
              type="submit"
              className="v36-btn v36-btn--neon v36-btn--lg"
              disabled={isLoading}
              style={{ width: '100%', marginTop: 4, opacity: isLoading ? 0.6 : 1 }}
            >
              {isLoading ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'v36-spin 0.8s linear infinite',
                    }}
                  />
                  <span>로그인 중...</span>
                </>
              ) : (
                <>
                  <V3Icon name="bolt" size={14} />
                  <span>로그인</span>
                </>
              )}
            </button>

            {/* 구분선 */}
            <div
              style={{
                position: 'relative',
                margin: '14px 0 6px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  height: 1,
                  background: 'var(--v36-line)',
                }}
              />
              <span
                className="v36-mono"
                style={{
                  position: 'relative',
                  padding: '0 12px',
                  background: 'var(--v36-bg-2)',
                  fontSize: 10,
                  color: 'var(--v36-ink-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                }}
              >
                또는 소셜 계정
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button
                type="button"
                onClick={() => socialLogin('kakao')}
                className="v36-btn"
                style={{
                  background: '#FEE500',
                  color: '#3C1E1E',
                  borderColor: '#FEE500',
                  fontWeight: 600,
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 3C6.477 3 2 6.463 2 10.691c0 2.724 1.8 5.119 4.508 6.49-.199.725-.72 2.625-.825 3.03-.13.503.185.495.389.36.16-.106 2.545-1.727 3.578-2.428.77.112 1.565.171 2.35.171 5.523 0 10-3.463 10-7.691C22 6.463 17.523 3 12 3z" />
                </svg>
                <span>카카오</span>
              </button>
              <button
                type="button"
                onClick={() => socialLogin('google')}
                className="v36-btn"
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span>Google</span>
              </button>
            </div>
          </form>

          <div
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: '1px solid var(--v36-line)',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--v36-ink-3)' }}>
              아직 계정이 없으신가요?{' '}
            </span>
            <Link
              href="/signup"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--v36-violet)',
                textDecoration: 'none',
              }}
            >
              무료로 가입하기 →
            </Link>
          </div>
        </div>

        <p
          className="v36-mono"
          style={{
            marginTop: 24,
            textAlign: 'center',
            fontSize: 10,
            color: 'var(--v36-ink-5)',
            letterSpacing: '0.10em',
          }}
        >
          © {new Date().getFullYear()} CLIPFLOW
        </p>
      </div>
    </main>
  )
}

/** 라벨 + 입력 컨테이너 */
function FormField({
  label,
  htmlFor,
  children,
}: {
  label: React.ReactNode
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        htmlFor={htmlFor}
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--v36-ink-2)',
          letterSpacing: '-0.01em',
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  )
}
